import { black, background, text } from "@/theme/colors";
import { Box, styled } from "@mui/material";

export type TPillTabsOption<T> = {
  label: string;
  value: T;
};

export type TPillTabsProps<T> = {
  options: TPillTabsOption<T>[];
  value: T;
  onChange: (value: T) => void;
  disabled?: boolean;
};

export const PillTabs = <T,>({ options, value, onChange, disabled }: TPillTabsProps<T>) => {
  const handleTabClick = (tabValue: T) => () => {
    if (!disabled && value !== tabValue) {
      onChange(tabValue);
    }
  };

  const activeIndex = options.findIndex((option) => option.value === value);

  return (
    <PillContainer>
      <SlidingIndicator activeIndex={activeIndex >= 0 ? activeIndex : 0} />
      {options.map((option, idx) => (
        <PillButton
          key={idx}
          active={value === option.value}
          onClick={handleTabClick(option.value)}
          sx={{
            pointerEvents: disabled ? "none" : "auto",
            opacity: disabled ? 0.6 : 1,
          }}
        >
          {option.label}
        </PillButton>
      ))}
    </PillContainer>
  );
};

const PillContainer = styled(Box)`
  position: relative;
  display: inline-flex;
  background-color: ${black[99]};
  padding: 4px;
  border-radius: 9999px;
  align-items: center;
`;

const SlidingIndicator = styled(Box)<{ activeIndex: number }>`
  position: absolute;
  top: 4px;
  height: 37px;
  width: 116px;
  background-color: ${background.white};
  border-radius: 9999px;
  box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.1);
  left: ${({ activeIndex }) => `${activeIndex * 116 + 4}px`};
  transition: left 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 0;
`;

const PillButton = styled(Box)<{ active: boolean }>`
  position: relative;
  z-index: 1;
  height: 37px;
  width: 116px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  font-family: "Noto Sans JP", sans-serif;
  font-weight: 450;
  font-size: 14px;
  cursor: pointer;
  transition: color 0.2s ease-in-out;
  background-color: transparent;
  color: ${({ active }) => (active ? text.primary : text.secondary)};
  user-select: none;

  &:hover {
    background-color: transparent;
  }
`;
